/**
 * The crown's purse, in the shape the resource strip already reads.
 *
 * > *"the recourses menu should also appear in the campaign map, the same way
 * > it gets confusing that its different"*
 *
 * The strip in `hud.ts` is a settlement's: a chip per `ResourceKey`, a number,
 * a rate, and a drawn mark. On the realm map the same chips have to answer the
 * same question about the *other* purse — a crown's treasury and its levies —
 * and that purse is shaped nothing like a town's. It has no warehouse cap, no
 * famine, no per-second flow, and its income arrives eight times a year at a
 * council rather than continuously off a field.
 *
 * So this file is the adapter and it is deliberately the only one: `hud.ts`
 * asks for `chips` and `levy` and paints them with the code it already had.
 * Nothing here re-derives an income — `projectedIncome` is the simulation's own
 * forecast, the same one the province cards and the AI's arithmetic read, which
 * is the rule `ui/income.ts` was written to establish after four hand copies of
 * `doIncome` had drifted apart.
 *
 * **The rate is a month.** A crown's gold moves at a council and at nothing
 * else, so sampling a delta per second — which is what the town's chips do —
 * would read zero on 59 frames out of 60 and then jump. `/mo` on the chip is
 * not decoration; it is the units differing, and the strip says so.
 */

import { projectedIncome } from '../sim/campaign/turn.ts';
import { rawPoints } from '../sim/campaign/armies.ts';
import { RESOURCE_KEYS, type ResourceKey } from '../sim/types.ts';
import type { CampaignState } from '../sim/campaign/types.ts';

/** One chip on the strip, reading the crown's stores rather than the town's. */
export interface PurseChip {
  key: ResourceKey;
  /** Banked in the treasury now. */
  have: number;
  /** What the next council brings in, net of wages and hulls. */
  perMonth: number;
}

export interface RealmPurse {
  /** In `RESOURCE_KEYS` order, so the strip reads left to right as it does at home. */
  chips: PurseChip[];
  /**
   * Men, which the people chip carries. Two quantities of different kinds — a
   * bank and a monthly income into it — and the chip labels only one `/mo`
   * because that is the whole of the confusion the realm column's own note was
   * written about.
   */
  levy: { have: number; perMonth: number };
  hosts: number;
  points: number;
  holdings: number;
}

/**
 * The four the crown always has an opinion about, whether or not it has any.
 *
 * Gold pays hosts and hulls; the other three are what a harbour, a hull and a
 * fortification are priced in. A chip that vanishes the month you run out is a
 * chip that disappears exactly when the player needs to see the zero, so these
 * are always on the strip. Everything else appears only once the realm actually
 * holds some or earns some — the ladder runs to eighteen resources and a realm
 * touches a handful of them.
 */
const ALWAYS: readonly ResourceKey[] = ['timber', 'stone', 'iron', 'gold'];

/**
 * Once per campaign per frame at most.
 *
 * `projectedIncome` walks every province, builds the lane adjacency and nets
 * off every army and hull; the strip, the resource card and the people card all
 * want it, and `update` runs at 60 Hz. The key is the object identity plus the
 * turn, so a cached purse cannot outlive the council that changed it — and a
 * `WeakRef` so a campaign that has been dropped (a run quit to the menu, a save
 * loaded over the top) is not held alive by a cache.
 */
let cacheRef: WeakRef<CampaignState> | null = null;
let cacheKey = '';
let cached: RealmPurse | null = null;

export function realmPurse(c: CampaignState, nation: number): RealmPurse {
  const key = `${nation}#${c.turn}#${c.phase}`;
  if (cached && cacheKey === key && cacheRef?.deref() === c) return cached;
  const purse = computePurse(c, nation);
  cacheRef = new WeakRef(c);
  cacheKey = key;
  cached = purse;
  return purse;
}

function computePurse(c: CampaignState, nation: number): RealmPurse {
  const me = c.nations[nation];
  const income = projectedIncome(c, nation);
  const chips: PurseChip[] = [];
  for (const k of RESOURCE_KEYS) {
    const have = me ? me.treasury[k] : 0;
    // Gold is the one line `projectedIncome` reports on its own; everything
    // else arrives in `goods`, and a key with neither a balance nor a yield is
    // simply not part of this realm's economy.
    const perMonth =
      k === 'gold' ? income.gold : (income.goods.find((g) => g.key === k)?.amount ?? 0);
    if (!ALWAYS.includes(k) && have < 1 && Math.abs(perMonth) < 0.5) continue;
    chips.push({ key: k, have, perMonth });
  }

  let hosts = 0;
  let points = 0;
  for (const a of c.armies) {
    if (a.owner !== nation) continue;
    hosts++;
    points += rawPoints(a);
  }
  let holdings = 0;
  for (const p of c.provinces) if (p.owner === nation) holdings++;

  return {
    chips,
    levy: { have: me ? me.manpower : 0, perMonth: income.levy },
    hosts,
    points,
    holdings,
  };
}
